import React, { useCallback, useContext } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  Image,
  Platform,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import Svg, { Path } from 'react-native-svg';
import { signInFormStyles as s } from './SignInForm.styles';
import { SignInAuthScreenLayout } from '../../src/signIn/components/SignInAuthScreenLayout';
import { sanitizeEmailInput } from '../../src/helpers/sanitizeInput.helper';
import { getAssetUrl, ASSET_PATHS } from '../../constants/assets';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { LanguageContext } from '../../src/i18n';
import {
  SHOW_FACEBOOK_OAUTH_SIGN_IN_BUTTON,
  SHOW_MICROSOFT_OAUTH_SIGN_IN_BUTTON,
  APPLE_SIGN_IN_BUTTON_LABEL,
  APPLE_SIGN_IN_LOADING_A11Y,
} from '../../src/signIn/constants/signInForm.constants';
import { isAppleSignInUiSupported } from '../../src/signIn/helpers/appleAuth.helper';

const SIGN_IN_MONKEY_IMAGE_URI = getAssetUrl(ASSET_PATHS.SIGN_IN_MONKEY);
const SIGN_IN_PLACEHOLDER_COLOR = '#5A5A60';
const SIGN_IN_ACCENT_SPINNER_COLOR = '#A78BFA';

const GoogleLogo: React.FC = () => (
  <View style={s.googleLogoContainer}>
    <Svg width={20} height={20} viewBox="0 0 24 24">
      <Path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <Path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <Path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <Path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </Svg>
  </View>
);

const AppleLogo: React.FC = () => (
  <View style={s.appleLogoContainer}>
    <Svg width={18} height={18} viewBox="0 0 24 24">
      <Path
        fill="#FFFFFF"
        d="M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-2.06-.92-3.39-.89-1.74.03-3.35 1.01-4.25 2.57-1.81 3.14-.46 7.79 1.3 10.34.86 1.25 1.89 2.65 3.24 2.6 1.3-.05 1.79-.84 3.36-.84 1.57 0 2.01.84 3.39.81 1.4-.02 2.29-1.27 3.15-2.53.99-1.45 1.4-2.85 1.42-2.93-.03-.01-2.72-1.04-2.75-4.13zM14.6 4.7c.72-.87 1.2-2.08 1.07-3.28-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-1.09 3.18 1.15.09 2.32-.59 3.04-1.46z"
      />
    </Svg>
  </View>
);

const MicrosoftLogo: React.FC = () => (
  <View style={s.microsoftLogoContainer}>
    <Svg width={18} height={18} viewBox="0 0 21 21">
      <Path d="M0 0h10v10H0z" fill="#F25022" />
      <Path d="M11 0h10v10H11z" fill="#7FBA00" />
      <Path d="M0 11h10v10H0z" fill="#00A4EF" />
      <Path d="M11 11h10v10H11z" fill="#FFB900" />
    </Svg>
  </View>
);

interface SignInFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  loading: boolean;
  googleLoading: boolean;
  facebookLoading: boolean;
  appleLoading: boolean;
  microsoftLoading: boolean;
  forgotPasswordLoading?: boolean;
  guestLoading?: boolean;
  isSignUp: boolean;
  legalTermsAccepted?: boolean;
  onToggleLegalTerms?: () => void;
  onOpenTerms?: () => void;
  onOpenPrivacy?: () => void;
  marketingEmailOptIn?: boolean;
  onToggleMarketingEmailOptIn?: () => void;
  onSubmit: () => void;
  onToggleMode: () => void;
  onGoogleAuth: () => void;
  onFacebookAuth: () => void;
  onAppleAuth: () => void;
  onMicrosoftAuth: () => void;
  onForgotPassword?: () => void;
  onExploreAsGuest?: () => void;
  errors?: { email?: string; password?: string; terms?: string };
  infoBannerText?: string | null;
  testID?: string;
}

export const SignInForm: React.FC<SignInFormProps> = ({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  googleLoading,
  facebookLoading,
  appleLoading,
  microsoftLoading,
  isSignUp,
  legalTermsAccepted = false,
  onToggleLegalTerms,
  onOpenTerms,
  onOpenPrivacy,
  marketingEmailOptIn = false,
  onToggleMarketingEmailOptIn,
  onSubmit,
  onToggleMode,
  onGoogleAuth,
  onFacebookAuth,
  onAppleAuth,
  onMicrosoftAuth,
  onForgotPassword,
  forgotPasswordLoading = false,
  guestLoading = false,
  onExploreAsGuest,
  errors = {},
  infoBannerText = null,
  testID,
}) => {
  const languageContext = useContext(LanguageContext);
  if (!languageContext) {
    throw new Error('SignInForm must be used within LanguageProvider');
  }
  const { t } = languageContext;
  const signInCopy = t.signIn;

  // SECURITY: Sanitize email input (allows @ but blocks all other dangerous chars)
  const handleEmailChange = useCallback(
    (text: string) => setEmail(sanitizeEmailInput(text)),
    [setEmail],
  );

  // SECURITY: Do NOT sanitize passwords - allow all characters
  const handlePasswordChange = useCallback(
    (text: string) => setPassword(text),
    [setPassword],
  );

  const showAppleSignInButton = isAppleSignInUiSupported();
  const anyLoading = loading || googleLoading || facebookLoading || microsoftLoading || (showAppleSignInButton && appleLoading) || forgotPasswordLoading || guestLoading;

  return (
    <SignInAuthScreenLayout testID={testID || 'sign-in-form'}>
      {/* Monkey sits on top of the card — sibling before card, pulled down by marginBottom */}
      <View style={s.monkeyWrapper}>
        <Image
          source={{ uri: SIGN_IN_MONKEY_IMAGE_URI }}
          style={s.monkeyImage}
          resizeMode="contain"
        />
      </View>

      <View style={s.card} testID={testID ? `${testID}-card` : 'sign-in-form-card'}>

          <Text style={s.brand}>
            <Text style={s.brandAccent}>Z</Text>porti
          </Text>
          <Text style={s.subtitle}>
            {isSignUp ? signInCopy.signUpSubtitle : signInCopy.signInSubtitle}
          </Text>

          {infoBannerText ? (
            <View
              style={s.infoBanner}
              accessibilityRole="text"
              testID={testID ? `${testID}-sign-in-info-banner` : 'sign-in-info-banner'}
            >
              <Text style={s.infoBannerText}>{infoBannerText}</Text>
            </View>
          ) : null}

          <View style={s.inputContainer}>
            <TextInput
              style={[s.input, errors.email ? s.inputError : undefined]}
              placeholder={signInCopy.emailPlaceholder}
              placeholderTextColor={SIGN_IN_PLACEHOLDER_COLOR}
              value={email}
              onChangeText={handleEmailChange}
              autoCapitalize="none"
              keyboardType="email-address"
              autoComplete="email"
              accessibilityLabel={signInCopy.emailPlaceholder}
              testID={testID ? `${testID}-email-input` : 'sign-in-form-email-input'}
            />
            {errors.email ? (
              <View style={s.errorContainer}>
                <Text style={s.errorText}>{errors.email}</Text>
              </View>
            ) : null}
          </View>

          <View style={s.inputContainer}>
            <TextInput
              style={[s.input, errors.password ? s.inputError : undefined]}
              placeholder={signInCopy.passwordPlaceholder}
              placeholderTextColor={SIGN_IN_PLACEHOLDER_COLOR}
              value={password}
              onChangeText={handlePasswordChange}
              secureTextEntry
              autoCapitalize="none"
              autoComplete={isSignUp ? 'password-new' : 'password'}
              accessibilityLabel={signInCopy.passwordPlaceholder}
              testID={testID ? `${testID}-password-input` : 'sign-in-form-password-input'}
            />
            {errors.password ? (
              <View style={s.errorContainer}>
                <Text style={s.errorText}>{errors.password}</Text>
              </View>
            ) : null}
          </View>

          {!isSignUp && onForgotPassword && (
            <Pressable
              style={s.forgotPasswordButton}
              onPress={onForgotPassword}
              disabled={anyLoading}
            >
              {forgotPasswordLoading
                ? <ActivityIndicator size="small" color={SIGN_IN_ACCENT_SPINNER_COLOR} accessibilityLabel={signInCopy.sendingResetEmail} />
                : <Text style={s.forgotPasswordText}>{signInCopy.forgotPassword}</Text>
              }
            </Pressable>
          )}

          {isSignUp && onToggleLegalTerms && onOpenTerms && onOpenPrivacy ? (
            <View
              style={[
                s.legalConsentButton,
                errors.terms ? s.legalConsentButtonInvalid : undefined,
              ]}
            >
              <Pressable
                style={({ pressed }) => [
                  s.legalConsentRow,
                  pressed && s.legalConsentButtonPressed,
                ]}
                onPress={onToggleLegalTerms}
                disabled={anyLoading}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: legalTermsAccepted }}
                testID={testID ? `${testID}-legal-terms-row` : 'sign-in-form-legal-terms-row'}
              >
                <View
                  style={[
                    s.marketingConsentCheckbox,
                    legalTermsAccepted && s.marketingConsentCheckboxChecked,
                  ]}
                >
                  {legalTermsAccepted ? <Text style={s.marketingConsentCheckmark}>✓</Text> : null}
                </View>
                <View style={s.legalConsentTextWrap}>
                  <Text style={s.legalConsentPlain}>{signInCopy.legalConsentPrefix}</Text>
                  <Pressable
                    onPress={onOpenTerms}
                    disabled={anyLoading}
                    accessibilityRole="link"
                    accessibilityLabel={t.footer.termsOfService}
                    testID={testID ? `${testID}-terms-link` : 'sign-in-form-terms-link'}
                  >
                    {({ pressed }) => (
                      <Text
                        style={[s.legalConsentLink, pressed && s.legalConsentLinkPressed]}
                      >
                        {t.footer.termsOfService}
                      </Text>
                    )}
                  </Pressable>
                  <Text style={s.legalConsentPlain}>{signInCopy.legalConsentAnd}</Text>
                  <Pressable
                    onPress={onOpenPrivacy}
                    disabled={anyLoading}
                    accessibilityRole="link"
                    accessibilityLabel={t.footer.privacyPolicy}
                    testID={testID ? `${testID}-privacy-link` : 'sign-in-form-privacy-link'}
                  >
                    {({ pressed }) => (
                      <Text
                        style={[s.legalConsentLink, pressed && s.legalConsentLinkPressed]}
                      >
                        {t.footer.privacyPolicy}
                      </Text>
                    )}
                  </Pressable>
                  <Text style={s.legalConsentPlain}>{signInCopy.legalConsentSuffix}</Text>
                </View>
              </Pressable>
              {errors.terms ? (
                <View style={s.termsErrorContainer} accessibilityLiveRegion="polite">
                  <Text style={s.errorText}>{errors.terms}</Text>
                </View>
              ) : null}
            </View>
          ) : null}

          {isSignUp && onToggleMarketingEmailOptIn ? (
            <Pressable
              style={({ pressed }) => [
                s.marketingConsentButton,
                pressed && s.marketingConsentButtonPressed,
              ]}
              onPress={onToggleMarketingEmailOptIn}
              disabled={anyLoading}
            >
              <View style={s.marketingConsentRow}>
                <View
                  style={[
                    s.marketingConsentCheckbox,
                    marketingEmailOptIn && s.marketingConsentCheckboxChecked,
                  ]}
                >
                  {marketingEmailOptIn ? <Text style={s.marketingConsentCheckmark}>✓</Text> : null}
                </View>
                <Text style={s.marketingConsentText}>
                  {signInCopy.marketingConsent}
                </Text>
              </View>
            </Pressable>
          ) : null}

          <Pressable
            style={({ pressed, hovered }) => [
              s.primaryButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE as never }),
              pressed && s.primaryButtonPressed,
              hovered && Platform.OS === 'web' && s.primaryButtonHover,
            ]}
            onPress={onSubmit}
            disabled={anyLoading}
            testID={testID ? `${testID}-submit-button` : 'sign-in-form-submit-button'}
          >
            {loading
              ? <ActivityIndicator color={SIGN_IN_ACCENT_SPINNER_COLOR} accessibilityLabel={isSignUp ? signInCopy.signingUp : signInCopy.signingIn} />
              : <Text style={s.primaryButtonText}>{isSignUp ? signInCopy.signUpButton : signInCopy.signInButton}</Text>
            }
          </Pressable>

          <Pressable
            style={s.toggleButton}
            onPress={onToggleMode}
            disabled={anyLoading}
          >
            <Text style={s.toggleText}>
              {isSignUp ? signInCopy.alreadyHaveAccount : signInCopy.noAccount}
              <Text style={s.toggleTextAccent}>
                {isSignUp ? signInCopy.toggleSignIn : signInCopy.toggleSignUp}
              </Text>
            </Text>
          </Pressable>

          <View style={s.divider}>
            <View style={s.dividerLine} />
            <Text style={s.dividerText}>{`${signInCopy.orDivider} ${signInCopy.signInWith}`}</Text>
            <View style={s.dividerLine} />
          </View>

          <View style={s.socialRow}>
            {showAppleSignInButton ? (
              <Pressable
                style={({ pressed, hovered }) => [
                  s.socialIconButton,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE as never }),
                  pressed && s.socialButtonPressed,
                  hovered && Platform.OS === 'web' && s.socialButtonHover,
                ]}
                onPress={onAppleAuth}
                disabled={anyLoading}
                accessibilityRole="button"
                accessibilityLabel={APPLE_SIGN_IN_BUTTON_LABEL}
                testID={testID ? `${testID}-apple-button` : 'sign-in-form-apple-button'}
              >
                {appleLoading
                  ? <ActivityIndicator color={SIGN_IN_ACCENT_SPINNER_COLOR} accessibilityLabel={APPLE_SIGN_IN_LOADING_A11Y} />
                  : <AppleLogo />
                }
              </Pressable>
            ) : null}

            <Pressable
              style={({ pressed, hovered }) => [
                s.socialIconButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE as never }),
                pressed && s.socialButtonPressed,
                hovered && Platform.OS === 'web' && s.socialButtonHover,
              ]}
              onPress={onGoogleAuth}
              disabled={anyLoading}
              accessibilityRole="button"
              accessibilityLabel={signInCopy.continuingWithGoogle}
              testID={testID ? `${testID}-google-button` : 'sign-in-form-google-button'}
            >
              {googleLoading
                ? <ActivityIndicator color={SIGN_IN_ACCENT_SPINNER_COLOR} accessibilityLabel={signInCopy.continuingWithGoogle} />
                : <GoogleLogo />
              }
            </Pressable>

            {SHOW_FACEBOOK_OAUTH_SIGN_IN_BUTTON ? (
              <Pressable
                style={({ pressed, hovered }) => [
                  s.socialIconButton,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE as never }),
                  pressed && s.socialButtonPressed,
                  hovered && Platform.OS === 'web' && s.socialButtonHover,
                ]}
                onPress={onFacebookAuth}
                disabled={anyLoading}
                accessibilityRole="button"
                accessibilityLabel={signInCopy.continuingWithFacebook}
              >
                {facebookLoading
                  ? <ActivityIndicator color={SIGN_IN_ACCENT_SPINNER_COLOR} accessibilityLabel={signInCopy.continuingWithFacebook} />
                  : <Ionicons name="logo-facebook" size={20} color="#FFFFFF" />
                }
              </Pressable>
            ) : null}

            {SHOW_MICROSOFT_OAUTH_SIGN_IN_BUTTON ? (
              <Pressable
                style={({ pressed, hovered }) => [
                  s.socialIconButton,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE as never }),
                  pressed && s.socialButtonPressed,
                  hovered && Platform.OS === 'web' && s.socialButtonHover,
                ]}
                onPress={onMicrosoftAuth}
                disabled={anyLoading}
                accessibilityRole="button"
                accessibilityLabel="Continue with Microsoft"
                testID={testID ? `${testID}-microsoft-button` : 'sign-in-form-microsoft-button'}
              >
                {microsoftLoading
                  ? <ActivityIndicator color={SIGN_IN_ACCENT_SPINNER_COLOR} accessibilityLabel="Signing in with Microsoft" />
                  : <MicrosoftLogo />
                }
              </Pressable>
            ) : null}
          </View>

          {onExploreAsGuest && (
            <Pressable
              style={({ pressed, hovered }) => [
                s.guestButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE as never }),
                pressed && s.guestButtonPressed,
                hovered && Platform.OS === 'web' && s.guestButtonHover,
              ]}
              onPress={onExploreAsGuest}
              disabled={anyLoading}
            >
              {guestLoading
                ? <ActivityIndicator size="small" color={SIGN_IN_ACCENT_SPINNER_COLOR} accessibilityLabel={signInCopy.enteringAsGuest} />
                : <Text style={s.guestButtonText}>{signInCopy.continueAsGuest}</Text>
              }
            </Pressable>
          )}

      </View>
    </SignInAuthScreenLayout>
  );
};
